/**
 * Optimizely Web Experimentation (OWE) Custom Event tracking
 *
 * Calls the OWE JavaScript API directly (not via GTM) so that OWE can
 * reliably receive custom conversion events.
 *
 * Tracking is gated on the presence of the data-owe-enabled attribute on
 * <body>, which is rendered when OptimizelyWebExperimentationProjectId is set
 * in Site Settings (i.e. when the OWE snippet is active).
 *
 * Docs: https://docs.developers.optimizely.com/web-experimentation/reference/event
 */

const isEnabled = (): boolean => document.body.hasAttribute('data-owe-enabled');

function pushEvent(eventName: string): void {
  if (!isEnabled()) return;
  window.optimizely = window.optimizely || [];
  window.optimizely.push({ type: 'event', eventName });
}

// ─── Public tracking helpers ─────────────────────────────────────────────────

export function trackFormSubmission(): void {
  pushEvent('lead___form_submission');
}

export function trackPdfDownload(): void {
  pushEvent('pdf_downloads');
}

export function trackPhoneClick(): void {
  pushEvent('lead___phone_clicks');
}

// ─── Delegated listeners ──────────────────────────────────────────────────────
// PDF and phone clicks are tracked via event delegation so that no individual
// Razor view or React component needs to be modified.

// window.optimizely.push() only queues the event locally — OWE's snippet still
// needs a brief moment to read the array and flush a network request. When a
// PDF link navigates the *current* tab, the page can unload before that
// request completes, silently dropping the event. Delaying the navigation
// gives OWE time to send it. This is not needed when the link already opens
// in a new tab/window (target="_blank", modified click, or a native
// `download` attribute) since the current page is never unloaded in those cases.
const NAVIGATION_DELAY_MS = 200;

const opensInCurrentTab = (e: MouseEvent, anchor: HTMLAnchorElement): boolean =>
  anchor.target !== '_blank' &&
  !anchor.hasAttribute('download') &&
  !e.metaKey &&
  !e.ctrlKey &&
  !e.shiftKey &&
  !e.altKey &&
  e.button !== 1; // middle-click

function trackThenNavigate(e: MouseEvent, anchor: HTMLAnchorElement, track: () => void): void {
  if (!opensInCurrentTab(e, anchor)) {
    // New tab / window, or a download attribute — current page isn't unloaded,
    // so there's no race with OWE's flush. Track and let the browser proceed natively.
    track();
    return;
  }

  e.preventDefault();
  track();

  const href = anchor.href;
  window.setTimeout(() => {
    window.location.href = href;
  }, NAVIGATION_DELAY_MS);
}

function initDelegatedListeners(): void {
  const handleAnchorInteraction = (e: MouseEvent): void => {
    const target = e.target as Element;
    const anchor = target.closest<HTMLAnchorElement>('a[href]');
    if (!anchor) return;

    const href = anchor.getAttribute('href') ?? '';

    // Phone clicks: any tel: link. Dialling doesn't unload the page, so no delay needed.
    if (href.startsWith('tel:')) {
      trackPhoneClick();
      return;
    }

    // PDF downloads: anchors pointing at a .pdf file.
    // Use anchor.pathname (the browser-resolved URL path) rather than the raw
    // href string, since content URLs can carry query strings (e.g.
    // ?epieditmode=False, culture/cache-busting params) that would break a
    // plain endsWith('.pdf') check on the raw attribute value.
    const path = (anchor.pathname || href).toLowerCase();
    if (!path.endsWith('.pdf')) return;

    trackThenNavigate(e, anchor, trackPdfDownload);
  };

  // 'click' does not fire for middle-mouse-button clicks in most browsers —
  // those dispatch 'auxclick' instead, so both must be listened for.
  document.addEventListener('click', handleAnchorInteraction);
  document.addEventListener('auxclick', handleAnchorInteraction);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDelegatedListeners);
} else {
  initDelegatedListeners();
}
